/**
 * AIMS Data Model Generator documentation content.
 * End-user guide for generating and managing AIMS data models in PostGIS.
 */
const C = (title, html) => ({ title, html });

export const aimsContent = {
  introduction: C(
    "1 Introduction",
    `<p>The AIMS Data Model Generator is a web-based tool that creates and manages AIMS (Asset Information Management System) data models in a target PostGIS database. It provides a guided workflow to configure database connections, select schemas and categories, and generate either the full data model directly in the database or a standalone SQL script for manual deployment.</p>
<h2>Key Capabilities</h2>
<ul>
<li><strong>Generate Data Model</strong> — A 4-step wizard that connects to PostgreSQL/PostGIS, creates or selects a database, defines a schema, selects AIMS categories, and deploys the model.</li>
<li><strong>Generate SQL Script</strong> — Produces a downloadable <code>.sql</code> file containing the schema DDL for selected categories, without requiring a live database connection.</li>
<li><strong>Schema Viewer</strong> — Inspect tables, enums, and relationships after generation.</li>
<li><strong>Saved Configurations</strong> — Store and reuse connection settings for quick access.</li>
</ul>
<p>This documentation is intended for end users who need to generate or update AIMS data models in their PostGIS environment.</p>`
  ),

  prerequisites: C(
    "2.1 Prerequisites",
    `<p>Before using the AIMS Data Model Generator, ensure the following:</p>
<ul>
<li><strong>PostgreSQL</strong> — A PostgreSQL server (typically 12 or later) with PostGIS extension support.</li>
<li><strong>PostGIS Extension</strong> — The PostGIS extension must be available and enabled in the target database.</li>
<li><strong>Network Access</strong> — The machine running the application must be able to reach the PostgreSQL server on the configured host and port.</li>
<li><strong>Credentials</strong> — Valid database user credentials with sufficient privileges to create databases, schemas, and objects (or at least to run the generated SQL).</li>
<li><strong>Browser</strong> — A modern web browser (Chrome, Firefox, Edge, Safari).</li>
</ul>
<p>If OAuth is enabled, you will also need access to the SEPM authentication service.</p>`
  ),

  installation: C(
    "2.2 Installation",
    `<p>The AIMS Data Model Generator can be run in two ways:</p>
<h3>Option A: Python Application</h3>
<p>If you have the Python source:</p>
<ol>
<li>Ensure Python 3.8+ is installed.</li>
<li>Install dependencies: <code>pip install -r requirements.txt</code></li>
<li>Configure environment variables (e.g. <code>.env</code>) if needed for OAuth or database defaults.</li>
<li>Start the server: <code>uvicorn main:app --host 0.0.0.0 --port 8000</code></li>
</ol>
<h3>Option B: Desktop / Packaged Application</h3>
<p>If distributed as a desktop application, follow the installer instructions. The application typically starts a local web server and opens the UI in your default browser.</p>
<p>For detailed setup, refer to the <code>QUICKSTART.md</code> or installation guide provided with your distribution.</p>`
  ),

  launching: C(
    "2.3 Launching the Application",
    `<p>After installation:</p>
<ol>
<li>Start the application (Python: run <code>uvicorn main:app</code>; Desktop: launch the executable).</li>
<li>Open your browser and navigate to the URL shown (e.g. <code>http://localhost:8000</code>).</li>
<li>If OAuth is enabled, you may be prompted to log in before accessing the main interface.</li>
</ol>
<p>The main interface displays two primary tabs: <strong>Generate Data Model</strong> and <strong>Generate SQL</strong>.</p>`
  ),

  "ui-overview": C(
    "3.1 Overview",
    `<p>The user interface is organized into:</p>
<ul>
<li><strong>Top Bar</strong> — Login status, language selector (EN/DE/FR), and theme toggle (light/dark).</li>
<li><strong>Main Tabs</strong> — <em>Generate Data Model</em> and <em>Generate SQL</em>.</li>
<li><strong>Content Area</strong> — Wizard steps or forms depending on the selected tab.</li>
<li><strong>Log Console</strong> — Output messages from generation operations (in Generate Data Model tab).</li>
<li><strong>Schema Viewer</strong> — Tree view of tables, enums, and relationships after a successful generation.</li>
</ul>`
  ),

  "top-bar": C(
    "3.2 Top Bar",
    `<p>The top bar provides:</p>
<ul>
<li><strong>Login / Logout</strong> — When OAuth is enabled, shows the current user and allows logout.</li>
<li><strong>Language</strong> — Switch between English (EN), German (DE), and French (FR).</li>
<li><strong>Theme</strong> — Toggle between light and dark mode.</li>
</ul>
<p>These settings persist across sessions where supported.</p>`
  ),

  tabs: C(
    "3.3 Main Tabs",
    `<p><strong>Generate Data Model</strong> — Full workflow to connect to a database and deploy the AIMS schema. Includes a 4-step wizard, saved configs, log console, and schema viewer.</p>
<p><strong>Generate SQL</strong> — Configure schema name and categories, then download a SQL script file. No database connection is required.</p>`
  ),

  "step1-connection": C(
    "4.1 Step 1 — Server Connection",
    `<p>In the first step of the Generate Data Model wizard, configure the PostgreSQL server connection:</p>
<ul>
<li><strong>Host</strong> — Server hostname or IP address (e.g. <code>localhost</code> or <code>192.168.1.10</code>).</li>
<li><strong>Port</strong> — PostgreSQL port (default <code>5432</code>).</li>
<li><strong>Username</strong> — Database user name.</li>
<li><strong>Password</strong> — Database password.</li>
</ul>
<p>Use <strong>Test Connection</strong> to verify the settings before proceeding. If the test succeeds, the <strong>Next</strong> button becomes available.</p>
<p>You can save the configuration for later use via the saved configs feature.</p>`
  ),

  "step2-database": C(
    "4.2 Step 2 — Database",
    `<p>Select or create the target database:</p>
<ul>
<li><strong>Existing Database</strong> — Choose from a dropdown of databases on the server.</li>
<li><strong>Create New Database</strong> — Enter a new database name. The tool will create it if the user has sufficient privileges.</li>
</ul>
<p>Ensure the database has the PostGIS extension enabled. If not, you may need to run <code>CREATE EXTENSION postgis;</code> manually or use an admin account.</p>
<p>Click <strong>Next</strong> to continue to the schema step.</p>`
  ),

  "step3-schema": C(
    "4.3 Step 3 — Schema",
    `<p>Define the schema where the AIMS objects will be created:</p>
<ul>
<li><strong>Schema Name</strong> — Enter the schema name (e.g. <code>aims</code> or <code>public</code>).</li>
<li><strong>Create Schema</strong> — Optionally create the schema if it does not exist.</li>
</ul>
<p>If the schema already exists, the generator will add or update objects within it. Be aware that re-running generation may alter or replace existing objects.</p>
<p>Click <strong>Next</strong> to proceed to category selection.</p>`
  ),

  "step4-categories": C(
    "4.4 Step 4 — Categories",
    `<p>Select which AIMS categories to include in the data model:</p>
<ul>
<li><strong>General (xge)</strong> — Base objects and common structures.</li>
<li><strong>Support (xsu)</strong> — Poles, trenches, manholes, and related support infrastructure.</li>
<li><strong>Electric (xel)</strong> — Cables, joints, services, lights, stations, and electrical assets.</li>
<li><strong>Other categories</strong> — Additional categories (xtr, xtc, xdr, xga, xwa, xht, xrd, xpi, xcp, xmu) may be listed; some may not yet be fully implemented.</li>
</ul>
<p>Select the categories you need. At least one category is typically required. Click <strong>Generate Data Model</strong> to start the deployment.</p>`
  ),

  "saved-configs": C(
    "4.5 Saved Connection Configs",
    `<p>You can save connection configurations for quick reuse:</p>
<ul>
<li>After configuring Step 1 (and optionally Step 2), use the <strong>Save Config</strong> option to store the current settings under a name.</li>
<li>Saved configs appear in a list or dropdown. Select one to load its values.</li>
<li>You can update or delete saved configs as needed.</li>
</ul>
<p>Saved configs are stored locally (e.g. in browser storage or application data) and are specific to the machine and user.</p>`
  ),

  "generate-action": C(
    "4.6 Generate Data Model",
    `<p>After completing all four steps:</p>
<ol>
<li>Review your selections (connection, database, schema, categories).</li>
<li>Click <strong>Generate Data Model</strong>.</li>
<li>The application connects to the database and creates the schema objects (tables, enums, indexes, etc.).</li>
<li>Progress and messages appear in the <strong>Log Console</strong>.</li>
<li>On success, the <strong>Schema Viewer</strong> updates to show the generated structure.</li>
</ol>
<p>If errors occur, check the log for details. Common issues include missing PostGIS, insufficient permissions, or invalid connection parameters.</p>`
  ),

  "log-console": C(
    "4.7 Log Console",
    `<p>The Log Console displays output from the generation process:</p>
<ul>
<li>Connection and validation messages.</li>
<li>SQL statements executed (if logging is enabled).</li>
<li>Success and error messages.</li>
</ul>
<p>Use the log to troubleshoot failures and verify that expected objects were created.</p>`
  ),

  "schema-viewer": C(
    "4.8 Schema Viewer",
    `<p>After a successful generation, the Schema Viewer shows:</p>
<ul>
<li><strong>Tables</strong> — All tables created in the selected schema.</li>
<li><strong>Enums</strong> — Enumeration types used by the schema.</li>
<li><strong>Relationships</strong> — Foreign key and other relationships between tables.</li>
</ul>
<p>Expand the tree nodes to inspect individual objects. This helps verify the model structure and understand dependencies.</p>`
  ),

  "sql-overview": C(
    "5.1 Overview",
    `<p>The <strong>Generate SQL</strong> tab produces a standalone SQL script file containing the DDL for the AIMS data model. No database connection is required. This is useful when:</p>
<ul>
<li>You want to review the SQL before applying it.</li>
<li>You need to run the script manually or through a deployment tool.</li>
<li>You do not have direct database access from the application.</li>
</ul>`
  ),

  "sql-workflow": C(
    "5.2 Workflow",
    `<p>To generate a SQL script:</p>
<ol>
<li>Switch to the <strong>Generate SQL</strong> tab.</li>
<li>Enter the <strong>Schema Name</strong> (e.g. <code>aims</code>).</li>
<li>Select the <strong>Categories</strong> to include (same options as in Generate Data Model).</li>
<li>Click <strong>Generate SQL</strong> or <strong>Download SQL</strong>.</li>
<li>Save the downloaded <code>.sql</code> file to your computer.</li>
</ol>
<p>The script includes <code>CREATE EXTENSION IF NOT EXISTS postgis;</code> (if applicable), schema creation, and all table, enum, and constraint definitions for the selected categories.</p>`
  ),

  "sql-apply": C(
    "5.3 Applying the Script",
    `<p>To apply the generated SQL script to your database:</p>
<ol>
<li>Connect to your PostgreSQL server using <code>psql</code>, pgAdmin, or another client.</li>
<li>Ensure the target database has PostGIS installed: <code>CREATE EXTENSION IF NOT EXISTS postgis;</code></li>
<li>Run the script: <code>psql -d your_database -f generated_aims.sql</code></li>
<li>Verify the schema and tables were created as expected.</li>
</ol>
<p>If the schema or objects already exist, the script may fail or require modification. Consider backing up your database before applying.</p>`
  ),

  "categories-overview": C(
    "6.1 Overview",
    `<p>AIMS data models are organized into categories. Each category defines a set of tables and types for a specific domain. The following categories are commonly available:</p>
<table class="min-w-full border border-gray-300 dark:border-gray-600 mt-4 mb-4">
<thead><tr><th class="border px-3 py-2 text-left">Code</th><th class="border px-3 py-2 text-left">Name</th><th class="border px-3 py-2 text-left">Description</th></tr></thead>
<tbody>
<tr><td class="border px-3 py-2">xge</td><td>General</td><td>Base objects and common structures</td></tr>
<tr><td class="border px-3 py-2">xsu</td><td>Support</td><td>Poles, trenches, manholes</td></tr>
<tr><td class="border px-3 py-2">xel</td><td>Electric</td><td>Cables, joints, services, lights, stations</td></tr>
<tr><td class="border px-3 py-2">xtr, xtc, xdr, xga, xwa, xht, xrd, xpi, xcp, xmu</td><td>Other</td><td>Additional domains; some may be planned or not yet implemented</td></tr>
</tbody>
</table>
<p>Select only the categories you need to keep the schema manageable.</p>`
  ),

  "general-category": C(
    "6.2 General (xge)",
    `<p>The General category (xge) provides foundational objects used across other categories. It typically includes base geometry types, common attributes, and shared reference data. This category is often required when other categories depend on it.</p>`
  ),

  "support-category": C(
    "6.3 Support (xsu)",
    `<p>The Support category (xsu) covers physical support infrastructure:</p>
<ul>
<li><strong>Poles</strong> — Utility poles and similar structures.</li>
<li><strong>Trenches</strong> — Trench and duct runs.</li>
<li><strong>Manholes</strong> — Manholes and related structures.</li>
</ul>
<p>These objects are commonly used in utility and infrastructure management.</p>`
  ),

  "electric-category": C(
    "6.4 Electric (xel)",
    `<p>The Electric category (xel) covers electrical assets:</p>
<ul>
<li><strong>Cables</strong> — Cable runs and conductors.</li>
<li><strong>Joints</strong> — Splices and joints.</li>
<li><strong>Services</strong> — Service connections.</li>
<li><strong>Lights</strong> — Street lights and luminaires.</li>
<li><strong>Stations</strong> — Substations and related equipment.</li>
</ul>
<p>This category is typically used for electrical distribution and street lighting applications.</p>`
  ),

  "other-categories": C(
    "6.5 Other Categories",
    `<p>Additional categories (xtr, xtc, xdr, xga, xwa, xht, xrd, xpi, xcp, xmu) may be listed in the UI. Some of these may be planned for future releases or not yet fully implemented. Check the application interface and release notes for the current status of each category.</p>`
  ),

  "oauth-login": C(
    "7.1 OAuth Login",
    `<p>When OAuth is enabled, the application uses the SEPM authentication service:</p>
<ul>
<li>You will be redirected to the login page when accessing the application.</li>
<li>After successful authentication, you are returned to the main interface.</li>
<li>Your session remains active until you log out or the session expires.</li>
</ul>
<p>If OAuth is not configured, the application may run without login.</p>`
  ),

  "access-control": C(
    "7.2 Access Control",
    `<p>Access control depends on your deployment:</p>
<ul>
<li>When OAuth is enabled, only authenticated users can access the application.</li>
<li>Database access is controlled by the PostgreSQL user credentials you provide. Ensure these credentials have only the privileges necessary for the intended operations.</li>
<li>Saved connection configs may store credentials; protect them according to your security policies.</li>
</ul>`
  ),

  "connection-issues": C(
    "8.1 Connection Issues",
    `<p>If you cannot connect to the database:</p>
<ul>
<li>Verify the host and port are correct and reachable (e.g. <code>telnet host 5432</code>).</li>
<li>Check that PostgreSQL is running and accepting connections (<code>pg_hba.conf</code>).</li>
<li>Confirm the username and password are valid.</li>
<li>Ensure any firewall or network rules allow the connection.</li>
<li>If using SSL, verify the server configuration and client settings.</li>
</ul>`
  ),

  postgis: C(
    "8.2 PostGIS Extension",
    `<p>If generation fails with PostGIS-related errors:</p>
<ul>
<li>Ensure PostGIS is installed on the PostgreSQL server.</li>
<li>Enable the extension in the target database: <code>CREATE EXTENSION IF NOT EXISTS postgis;</code></li>
<li>Use a database user with privileges to create extensions, or ask an administrator to enable it.</li>
</ul>`
  ),

  permissions: C(
    "8.3 Permissions",
    `<p>If you see permission errors during generation:</p>
<ul>
<li>The database user must be able to create schemas, tables, types, and indexes.</li>
<li>To create a new database, the user typically needs <code>CREATEDB</code> privilege.</li>
<li>To create extensions, the user may need superuser or specific extension privileges.</li>
<li>Review the error message in the Log Console for the specific object that failed.</li>
</ul>`
  ),
};
